/**
 * PublicLayout.jsx
 * Layout reutilizable para TODAS las páginas públicas de uTurno.
 * Incluye: Navbar blanco sticky con menú "Rubros ▾" y Footer corporativo.
 * 
 * Se usa en: Home, NicheLanding, Terms, Privacy, /reserva, etc.
 * De esta forma todas las páginas públicas comparten el mismo estilo visual.
 */

import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaInstagram,
  FaCut,
  FaDog,
  FaWrench,
  FaBalanceScale,
  FaSpa,
  FaHeartbeat,
  FaFutbol,
  FaGraduationCap,
  FaBuilding,
  FaCamera,
  FaGamepad
} from './icons/faLite'; // TASK-157: fuera de react-icons/fa para no inflar main.js
import { useAuth } from '../context/authContext';
import '../styles/home.css'; // Estilos del navbar y dropdown

// Catálogo de rubros para el menú desplegable (3 columnas) con iconos profesionales React Icons
const RUBROS_MENU = [
  // Columna 1
  [
    { to: '/software-para-barberias', Icon: FaCut, title: 'Barberías / Peluquerías', sub: 'Agenda por profesional', color: '#6366f1' },
    { to: '/veterinaria', Icon: FaDog, title: 'Veterinarias', sub: 'Fichas por mascota', color: '#f59e0b' },
    { to: '/taller-mecanico', Icon: FaWrench, title: 'Talleres Mecánicos', sub: 'Gestión por patente', color: '#dc2626' },
    { to: '/consultoria', Icon: FaBalanceScale, title: 'Consultoría / Estudios', sub: 'Abogados, contadores', color: '#7c3aed' },
  ],
  // Columna 2
  [
    { to: '/software-para-estetica', Icon: FaSpa, title: 'Estética / Spa', sub: 'Tratamientos y camillas', color: '#ec4899' },
    { to: '/software-para-salud', Icon: FaHeartbeat, title: 'Salud / Consultorios', sub: 'Odontología, kinesiología', color: '#3b82f6' },
    { to: '/canchas', Icon: FaFutbol, title: 'Canchas / Deportes', sub: 'Fútbol, pádel, tenis', color: '#22c55e' },
    { to: '/academias', Icon: FaGraduationCap, title: 'Academias / Clases', sub: 'Cupos y matrículas', color: '#047857' },
  ],
  // Columna 3
  [
    { to: '/inmobiliaria', Icon: FaBuilding, title: 'Inmobiliarias', sub: 'Coordinación de visitas', color: '#ea580c' },
    { to: '/eventos', Icon: FaCamera, title: 'Eventos / Foto', sub: 'Salones y estudios', color: '#d946ef' },
    { to: '/gaming', Icon: FaGamepad, title: 'Gaming / Escape Rooms', sub: 'Reserva por hora', color: '#06b6d4' },
  ],
];


/**
 * Componente de Layout Público reutilizable.
 * Envuelve el contenido de la página con navbar y footer consistentes.
 * @param {React.ReactNode} children - Contenido de la página
 * @param {boolean} showFooter - Si se muestra el footer (default: true)
 */
// TASK-153: enlaces del footer a las páginas por intención de búsqueda
const FOOTER_SOLUTIONS = [
  { to: '/crm-para-negocios', label: 'CRM para negocios' },
  { to: '/sistema-de-turnos-online', label: 'Sistema de turnos online' },
  { to: '/turnero-online', label: 'Turnero online' },
  { to: '/agenda-online', label: 'Agenda online' },
  { to: '/software-para-salones-de-belleza', label: 'Salones de belleza' }, // TASK-167
  { to: '/software-para-peluquerias', label: 'Peluquerías' },
  { to: '/software-para-medicos', label: 'Consultorios médicos' },
  { to: '/software-para-psicologos', label: 'Psicólogos' },
  { to: '/software-para-odontologos', label: 'Odontólogos' },
  { to: '/software-para-kinesiologos', label: 'Kinesiólogos' },
  { to: '/software-para-nutricionistas', label: 'Nutricionistas' },
  { to: '/software-para-unas-y-manicura', label: 'Uñas y manicura' },
  { to: '/software-para-spa-y-masajes', label: 'Spa y masajes' },
  { to: '/software-para-tatuadores', label: 'Tatuadores' },
  { to: '/software-para-entrenadores-personales', label: 'Entrenadores' },
  { to: '/software-para-peluquerias-caninas', label: 'Peluquería canina' },
  { to: '/funcionalidades', label: 'Funcionalidades' },
  { to: '/precios', label: 'Precios' },
];

const PublicLayout = ({ children, showFooter = true }) => {
  const { isAuthenticated, user } = useAuth(); // Verificamos si el usuario está autenticado y su estado de onboarding
  const handleIngresarClick = (e) => {
    e.preventDefault();
    if (window.location.pathname === '/') {
      window.dispatchEvent(new CustomEvent('open-login-view'));
      const card = document.getElementById('login-card');
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          document.getElementById('loginEmail')?.focus();
        }, 200);
      }
    } else {
      window.location.href = '/?login=true#login-card';
    }
  };

  // Manejador para el botón "Prueba Gratis" del Navbar
  const handleRegisterClick = (e) => {
    e.preventDefault();
    if (window.location.pathname === '/') {
      window.dispatchEvent(new CustomEvent('open-register-view'));
      const card = document.getElementById('login-card');
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      window.location.href = '/?register=true#login-card';
    }
  };

  return (
    <>
      {/* ===== NAVBAR BLANCO STICKY (Compartido en todas las públicas) ===== */}
      <header className="sticky-top bg-white border-bottom" style={{ zIndex: 1030 }}>
        {/* Contenedor del Navbar con 16px exactos de margen lateral (px-3) para alinear con el contenido */}
        <Container className="d-flex align-items-center justify-content-between py-2 px-3">
          {/* Logo oficial transparente y marca uTurno */}
          <Link to="/" className="text-decoration-none d-flex align-items-center gap-2" style={{ minHeight: '44px' }}>
            <picture>
              <source srcSet="/logo-uturno.webp" type="image/webp" />
              <img
                src="/logo-uturno.png"
                alt="uTurno Logo"
                width="34"
                height="34"
                fetchPriority="high"
                decoding="async"
                style={{ width: '34px', height: '34px', objectFit: 'contain' }}
              />
            </picture>
            <span className="fw-bold text-dark d-flex align-items-center" style={{ fontSize: '1.3rem', letterSpacing: '-0.03em' }}>
              uTurno<span style={{ color: '#2a9d8f', fontWeight: 800 }}>.com</span>
            </span>
          </Link>

          {/* Navegación central con dropdown de rubros (visible en desktop) */}
          <nav className="d-none d-md-flex align-items-center gap-1">
            <a href="/#features" className="nav-link-home px-3 py-2 rounded-2 text-secondary text-decoration-none" style={{ fontSize: '0.875rem', fontWeight: 500 }}>Módulos</a>

            {/* Dropdown de Rubros con hover */}
            <div className="position-relative"
              onMouseEnter={(e) => e.currentTarget.querySelector('.rubros-dropdown').style.display = 'block'}
              onMouseLeave={(e) => e.currentTarget.querySelector('.rubros-dropdown').style.display = 'none'}
            >
              <button className="nav-link-home px-3 py-2 rounded-2 text-secondary border-0 bg-transparent d-flex align-items-center gap-1" style={{ fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>
                Rubros <span style={{ fontSize: '0.65rem' }}>▾</span>
              </button>

              {/* Panel desplegable con grilla de 3 columnas */}
              <div className="rubros-dropdown position-absolute bg-white border rounded-3 shadow-lg p-4"
                style={{ display: 'none', top: '100%', left: '50%', transform: 'translateX(-50%)', width: '680px', zIndex: 1050 }}
              >
                <div className="row g-0">
                  {RUBROS_MENU.map((column, colIdx) => (
                    <div className="col-4" key={colIdx}>
                      {column.map((rubro) => (
                        <Link key={rubro.to} to={rubro.to} className="d-flex align-items-start gap-2 p-2 rounded-2 text-decoration-none rubro-item">
                          <div className="rounded-2 p-1 d-flex align-items-center justify-content-center" style={{ backgroundColor: `${rubro.color}15`, minWidth: '28px', height: '28px' }}>
                            <rubro.Icon size={14} style={{ color: rubro.color }} />
                          </div>
                          <div>
                            <div className="fw-semibold text-dark" style={{ fontSize: '0.82rem' }}>{rubro.title}</div>
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>{rubro.sub}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <a href="/#pricing" className="nav-link-home px-3 py-2 rounded-2 text-secondary text-decoration-none" style={{ fontSize: '0.875rem', fontWeight: 500 }}>Precios</a>
          </nav>

          {/* Acciones del header con touch target accesible de 44px.
              TASK-161: text-nowrap + menos relleno en celular; antes "Comenzar Gratis" se partía en
              dos renglones en pantallas de ~375 px y el botón quedaba el doble de alto. En celular
              "Ingresar" se oculta: el formulario de ingreso ya es lo primero debajo del encabezado */}
          <div className="d-flex align-items-center gap-1 gap-sm-2 flex-shrink-0">
            {isAuthenticated ? (
              (() => {
                const needsOnboarding = user && (user.onboarding_completed === false || user.onboarding_completed === 0 || user.onboarding_completed === 'false');
                return (
                  <Link 
                    to={needsOnboarding ? '/onboarding' : '/dashboard'} 
                    className="btn btn-primary btn-sm px-3 d-inline-flex align-items-center justify-content-center" 
                    style={{ borderRadius: '8px', fontSize: '0.85rem', fontWeight: 500, minHeight: '44px' }}
                  >
                    {needsOnboarding ? 'Configurar Negocio' : 'Mi Panel'} <FaArrowRight className="ms-1" size={12} />
                  </Link>
                );
              })()
            ) : (
              <>
                <button 
                  onClick={handleIngresarClick} 
                  className="btn btn-sm text-secondary border-0 bg-transparent px-2 px-sm-3 text-nowrap d-none d-sm-inline-flex align-items-center justify-content-center"
                  style={{ fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer', minHeight: '44px' }}
                >
                  Ingresar
                </button>
                <button 
                  onClick={handleRegisterClick} 
                  className="btn btn-primary btn-sm px-2 px-sm-3 text-nowrap d-inline-flex align-items-center justify-content-center" 
                  style={{ borderRadius: '8px', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer', minHeight: '44px' }}
                >
                  Comenzar Gratis
                </button>
              </>
            )}
          </div>
        </Container>
      </header>

      {/* ===== CONTENIDO DE LA PÁGINA ===== */}
      {children}

      {/* ===== FOOTER CORPORATIVO CON ALINEACIÓN SIMÉTRICA 16PX ===== */}
      {showFooter && (
        <footer className="bg-dark text-white text-center py-4 border-top border-secondary border-opacity-25" style={{ paddingTop: '32px', paddingBottom: '24px' }}>
          <Container className="px-3">
            <Row>
              <Col md={12}>
                <div className="h4 fw-bold mb-3 d-flex align-items-center justify-content-center gap-2 text-white">
                  <div className="bg-primary rounded-circle" style={{ width: 12, height: 12 }}></div> uTurno
                </div>
                <p className="small text-white-50 mb-4" style={{ maxWidth: '450px', margin: '0 auto', lineHeight: 1.6 }}>
                  Agenda, turnos online y CRM de clientes para negocios de servicios en Argentina: reservas 24/7, señas con Mercado Pago y la ficha de cada cliente.
                </p>
                {/* TASK-153: enlaces internos a las landings por intención de búsqueda */}
                <nav aria-label="Soluciones" className="d-flex justify-content-center flex-wrap gap-1 mb-3 mx-auto" style={{ maxWidth: 860, fontSize: '0.8rem' }}>
                  {FOOTER_SOLUTIONS.map(({ to, label }) => (
                    <Link key={to} to={to} className="text-white-50 text-decoration-none px-2 d-inline-flex align-items-center" style={{ minHeight: '32px' }}>{label}</Link>
                  ))}
                </nav>
                <div className="d-flex justify-content-center gap-2 gap-sm-4 mb-4 small fw-medium text-white-50 flex-wrap">
                  <Link to="/reserva" className="text-white-50 text-decoration-none px-2 d-inline-flex align-items-center" style={{ minHeight: '44px' }}>Directorio de Locales</Link>
                  <Link to="/terms" className="text-white-50 text-decoration-none px-2 d-inline-flex align-items-center" style={{ minHeight: '44px' }}>Términos Legales</Link>
                  <Link to="/privacy" className="text-white-50 text-decoration-none px-2 d-inline-flex align-items-center" style={{ minHeight: '44px' }}>Privacidad</Link>
                </div>
                <div className="mb-4">
                  <a href="https://www.instagram.com/uturnoapp/" target="_blank" rel="noopener noreferrer"
                    className="btn btn-outline-light rounded-pill px-4 py-2 d-inline-flex align-items-center gap-2"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', minHeight: '44px' }}>
                    <FaInstagram size={18} /> <span className="small fw-bold">Seguinos en IG</span>
                  </a>
                </div>
                <p className="mb-0 text-white-50" style={{ fontSize: '0.75rem' }}>
                  &copy; {new Date().getFullYear()} uTurno App | Software de Turnos Online. Todos los derechos reservados.
                </p>
              </Col>
            </Row>
          </Container>
        </footer>
      )}
    </>
  );
};

export default PublicLayout;
